import React from 'react'
import { Navigate, Route, Routes } from 'react-router-dom'

import { ApiKeysTable } from 'components/profile/ApiKeysTable'
import { ProfileIndexRoutesWrapper as RoutesWrapper } from 'components/profile/ProfileIndexRoutesWrapper'
import { ProfilePage } from 'components/profile/ProfilePage'
import UserDetails from 'components/profile/UserDetails'
import UserIntegrations from 'components/profile/UserIntegrations'
import { UserSecurity } from 'components/profile/UserSecurity'
import { TrackedGremlinRouteElement } from 'components/TrackedGremlinRouteElement'
import useAuth from 'hooks/use-auth'
import { useGremlinQuery } from 'hooks/use-gremlin-query'
import { isPrivateEdition } from 'utils/app-config'
import { getMappedCompanyData } from 'utils/company'
import { ms } from 'utils/millisecond'

import type { ApiKey } from 'types/api/api-key'
import type { Company } from 'types/api/rbac/company'

const ProfileIndex: React.FC = () => {
  const { companyId } = useAuth()

  const {
    data: apiKeys,
    isError: isLoadingApiKeysError,
    isLoading: isLoadingApiKeys,
    refetch: refetchApiKeys
  } = useGremlinQuery<ApiKey[]>('/apikeys', {})

  const { data: companies, isFetching: isFetchingCompanies } = useGremlinQuery<
    Company[]
  >(
    `/companies/${companyId}`,
    {},
    { placeholderData: [], refetchInterval: ms.seconds(60) }
  )
  const company = getMappedCompanyData(companies)
  const areApiKeysEnabled = company?.auth_method_prefs?.api_keys_enabled

  return (
    <Routes>
      <Route element={<RoutesWrapper isLoading={isFetchingCompanies} />}>
        <Route
          element={
            <ProfilePage
              apiKeyCount={apiKeys?.length ?? 0}
              areApiKeysEnabled={areApiKeysEnabled}
            />
          }
        >
          <Route index element={<Navigate to="details" />} path="*" />
          <Route
            index
            element={
              <TrackedGremlinRouteElement
                loggedIn
                component={<UserDetails />}
                name="Details"
              />
            }
            path="details"
          />
          <Route
            element={
              <TrackedGremlinRouteElement
                loggedIn
                component={<UserSecurity />}
                name="Security"
              />
            }
            path="security"
          />
          {!isPrivateEdition() && (
            <Route
              element={
                <TrackedGremlinRouteElement
                  loggedIn
                  component={<UserIntegrations />}
                  name="Integrations"
                />
              }
              path="integrations"
            />
          )}
          {areApiKeysEnabled && (
            <Route
              element={
                <TrackedGremlinRouteElement
                  loggedIn
                  component={
                    <ApiKeysTable
                      apiKeysLimit={5}
                      doRefresh={refetchApiKeys}
                      hasError={isLoadingApiKeysError}
                      isLoading={isLoadingApiKeys}
                      keys={apiKeys ?? []}
                    />
                  }
                  name="API Keys"
                />
              }
              path="apikeys"
            />
          )}
        </Route>
      </Route>
    </Routes>
  )
}

// this needs to be a default export because it is a chunk loaded by webpack
export default ProfileIndex
